import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Ui/Button';

interface Props {
    onCancel: () => void;

    /** Optional submit label override; defaults to `forms.save`. */
    saveLabel?: ReactNode;

    /** Optional cancel label override; defaults to `forms.cancel`. */
    cancelLabel?: ReactNode;
}

/**
 * Standard dialog/page footer that reads form state from the surrounding
 * `<FormProvider>`. Mirrors the legacy `Ui/Form/StandardButtons.jsx` but
 * sits inside an RHF tree — `useForm()` lives in the parent (the dialog or
 * page), and this component plus the form-fields component are both
 * descendants of one `<FormProvider>`.
 *
 *   - Submit button calls the form's `handleSubmit(onSubmit)` (the parent
 *     attaches this via `<form onSubmit={...}>`; here we just submit the
 *     enclosing form via DOM).
 *   - Disabled when validating, invalid, or submitting.
 *   - The "validate-with-touched" icon calls `trigger()` to surface errors
 *     for fields the user hasn't touched yet.
 */
export function StandardButtons({ onCancel, saveLabel, cancelLabel }: Props) {
    const [
        t,
    ] = useTranslation('pages');
    const { formState: { isValid, isSubmitting }, trigger } = useFormContext();

    const canSubmit = isValid && !isSubmitting;

    return (
        <div className='flex justify-end items-center gap-2'>
            <Button
                onClick={ onCancel }
                type='button'
                variant='outline'
            >
                { cancelLabel ?? t('forms.cancel') }
            </Button>
            <Button
                disabled={ !canSubmit }
                type='submit'
            >
                { saveLabel ?? t('forms.save') }
            </Button>
            <div className='w-12'>
                { canSubmit && <CheckCircleIcon className='w-8 h-8 ml-4 text-success' /> }
                { !isValid && (
                    <InformationCircleIcon
                        className='w-8 h-8 ml-4 cursor-pointer text-destructive'
                        onClick={ () => {
                            trigger();
                        } }
                    />
                ) }
            </div>
        </div>
    );
}
